// Arithmetic over named inputs for calculated tests: numbers, + - * /,
// parentheses and unary minus, e.g. "tower_cond / makeup_cond". The formula is
// parsed into a tree and walked; it is never run as code. This mirrors the
// API's FormulaEvaluator so the number shown while typing matches the one the
// server saves.

const TOKEN = /\s*(?:(\d+\.?\d*|\.\d+)|([A-Za-z_][A-Za-z0-9_]*)|([-+*/()]))/y;

function tokenize(formula) {
  const text = `${formula ?? ''}`;
  const tokens = [];
  let position = 0;

  while (position < text.length) {
    if (/^\s*$/.test(text.slice(position))) break;
    TOKEN.lastIndex = position;
    const match = TOKEN.exec(text);
    if (!match) throw new Error(`Unexpected character ${text.slice(position).trim()[0]}`);

    if (match[1] !== undefined) tokens.push({ type: 'number', value: parseFloat(match[1]) });
    else if (match[2] !== undefined) tokens.push({ type: 'name', value: match[2] });
    else tokens.push({ type: 'symbol', value: match[3] });
    position = TOKEN.lastIndex;
  }

  return tokens;
}

function parse(formula) {
  const tokens = tokenize(formula);
  if (!tokens.length) throw new Error('Formula is empty');
  let position = 0;

  const symbol = () => (tokens[position]?.type === 'symbol' ? tokens[position].value : null);

  const value = () => {
    const token = tokens[position];
    if (!token) throw new Error('Formula ends too soon');
    position += 1;
    if (token.type === 'number' || token.type === 'name') return token;
    if (token.value !== '(') throw new Error(`Unexpected ${token.value}`);

    const inner = sum();
    if (symbol() !== ')') throw new Error('Missing )');
    position += 1;
    return inner;
  };

  const unary = () => {
    if (symbol() === '-') {
      position += 1;
      return { type: 'negate', value: unary() };
    }
    return value();
  };

  const product = () => {
    let left = unary();
    while (symbol() === '*' || symbol() === '/') {
      const operator = tokens[position].value;
      position += 1;
      left = { type: operator, left, right: unary() };
    }
    return left;
  };

  function sum() {
    let left = product();
    while (symbol() === '+' || symbol() === '-') {
      const operator = tokens[position].value;
      position += 1;
      left = { type: operator, left, right: product() };
    }
    return left;
  }

  const tree = sum();
  if (position < tokens.length) throw new Error(`Unexpected ${tokens[position].value}`);
  return { tree, tokens };
}

// A reading as a number, or null when it is blank or isn't one.
export function toNumber(value) {
  if (value === null || value === undefined || `${value}`.trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function walk(node, values) {
  switch (node.type) {
    case 'number': return node.value;
    case 'name': return toNumber(values[node.value]);
    case 'negate': {
      const inner = walk(node.value, values);
      return inner === null ? null : -inner;
    }
    default: {
      const left = walk(node.left, values);
      const right = walk(node.right, values);
      if (left === null || right === null) return null;
      if (node.type === '+') return left + right;
      if (node.type === '-') return left - right;
      if (node.type === '*') return left * right;
      return right === 0 ? null : left / right;
    }
  }
}

// Input names the formula uses, in order of first appearance.
// Throws with a readable message when the formula can't be parsed.
export function formulaVariables(formula) {
  const names = parse(formula).tokens.filter((t) => t.type === 'name').map((t) => t.value);
  return [...new Set(names)];
}

// The result for { name: number }, rounded to `decimals` places. Null when an
// input is missing, the formula can't be read, or the arithmetic has no
// answer (dividing by zero).
export function evaluateFormula(formula, values, decimals = 2) {
  try {
    const result = walk(parse(formula).tree, values || {});
    if (result === null || !Number.isFinite(result)) return null;
    const scale = 10 ** decimals;
    return Math.round(result * scale) / scale;
  } catch (error) {
    return null;
  }
}
